import { Modal, Dropdown } from 'react-bootstrap';
import { useExtAConfigs } from '../../hooks/useExtAConfigs';
import CloseButton from './closeButton';
import PivotTable from '../pivotTable';
import styled from 'styled-components';
import FetchErrorView from '../fetchErrorView';
import LoadingView from '../loadingView';
import { useMemo } from 'react';
import { exportGridToExcel, exportGridToCSV } from '../../../../helpers/excelExportUtils';
import { useSelector } from 'react-redux';
import type { RootState } from '../../../../state/store';

interface ReportDialogProps {
  show: boolean;
}

const ReportDialog = ({ show = false }: ReportDialogProps) => {
  const {
    reportConfigs,
    dialogHandler,
    loadState,
    savedVwsDialogHandler,
    saveCurrentViewDialogHandler,
    getPivotTableInstance,
    colDefs
  } = useExtAConfigs();

  const company = useSelector((state: RootState) => state.company);
  const timezoneOffset = company?.company_details?.time_zone_offset;

  const handleExportCSV = () => {
    const gridApi = getPivotTableInstance()?.api;
    if (!gridApi) {
      console.error('Grid API not available for export');
      return;
    }

    try {
      // Generate filename with current date
      const fileName = `${reportConfigs.title || 'Report'}_${
        new Date().toISOString().split('T')[0]
      }.csv`;

      // Use custom vanilla CSV export with flattened data
      exportGridToCSV(gridApi, fileName, colDefs, timezoneOffset);

      console.log('Exported flattened CSV file');
    } catch (error) {
      console.error('Error exporting CSV file:', error);
      // Show user-friendly error message
      alert('Failed to export CSV file. Please try again.');
    }
  };

  const handleExportExcel = () => {
    const gridApi = getPivotTableInstance()?.api;
    if (!gridApi) {
      console.error('Grid API not available for export');
      return;
    }

    try {
      // Generate filename with current date
      const fileName = `${reportConfigs.title || 'Report'}_${
        new Date().toISOString().split('T')[0]
      }.xlsx`;
      const sheetName = `Report`;

      // Use custom vanilla Excel export with flattened data
      exportGridToExcel(gridApi, fileName, sheetName, colDefs, timezoneOffset);

      console.log('Exported flattened Excel file with borders for non-empty cells');
    } catch (error) {
      console.error('Error exporting Excel file:', error);
      // Show user-friendly error message
      alert('Failed to export Excel file. Please try again.');
    }
  };

  // Chart creation handlers
  const createChart = (chartType: string) => {
    const gridApi = getPivotTableInstance()?.api;
    if (!gridApi) {
      console.error('Grid API not available for chart creation');
      return;
    }

    try {
      // Use the simpler createChart approach which automatically uses visible data
      gridApi.createPivotChart({
        chartType: chartType,
        suppressChartRanges: true, // Don't use cell ranges - use all visible data
        unlinkChart: false, // Keep the chart linked to the grid data
        chartThemeOverrides: {
          common: {
            title: {
              enabled: true,
              text: reportConfigs.title || 'Chart'
            },
            legend: {
              position: 'bottom'
            }
          }
        }
      });
    } catch (error) {
      console.error('Error creating chart:', error);
      alert('Could not create chart. Please ensure there is data displayed in the grid.');
    }
  };

  const handleCreateColumnChart = () => createChart('column');
  const handleCreateBarChart = () => createChart('bar');
  const handleCreateLineChart = () => createChart('line');
  const handleCreateAreaChart = () => createChart('area');
  const handleCreatePieChart = () => createChart('pie');
  const handleCreateScatterChart = () => createChart('scatter');

  const chartOptions = useMemo(() => {
    return [
      { label: 'Column Chart', onClick: handleCreateColumnChart },
      { label: 'Bar Chart', onClick: handleCreateBarChart },
      { label: 'Line Chart', onClick: handleCreateLineChart },
      { label: 'Area Chart', onClick: handleCreateAreaChart },
      { label: 'Pie Chart', onClick: handleCreatePieChart },
      { label: 'Scatter Chart', onClick: handleCreateScatterChart }
    ];
  }, []);

  return (
    <Modal show={show} size="lg" dialogClassName="custom curved-wide-report extended-a-report">
      <Modal.Header>
        <Modal.Title>{reportConfigs.title}</Modal.Title>
        {reportConfigs.configs ? (
          <ToolbarWrapper>
            <div className="export-dropdown-container">
              <Dropdown>
                <Dropdown.Toggle
                  id="export-dropdown"
                  className="btn btn-toolbar-item export-toggle"
                >
                  Export
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  <Dropdown.Item onClick={handleExportCSV}>CSV</Dropdown.Item>
                  <Dropdown.Item onClick={handleExportExcel}>Excel</Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            </div>

            <div className="export-dropdown-container">
              <Dropdown>
                <Dropdown.Toggle id="chart-dropdown" className="btn btn-toolbar-item export-toggle">
                  Pivot Chart
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  {chartOptions.map((option) => (
                    <Dropdown.Item key={option.label} onClick={option.onClick}>
                      {option.label}
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
            </div>

            <button
              className="btn btn-toolbar-item"
              style={{
                background: '#2b7bc5',
                color: '#ffffff',
                border: 'transparent'
              }}
              onClick={savedVwsDialogHandler.showDialog}
            >
              Saved Views
            </button>
            <button
              className="btn btn-toolbar-item"
              style={{
                background: '#565656',
                color: '#ffffff',
                border: 'transparent'
              }}
              onClick={saveCurrentViewDialogHandler.showDialog}
            >
              Save Current View
            </button>
          </ToolbarWrapper>
        ) : null}
        <CloseButton onClick={dialogHandler.hideReport} />
      </Modal.Header>
      <Modal.Body>
        {loadState.isLoading ? (
          <LoadingView />
        ) : loadState.isError ? (
          <FetchErrorView errorType={'fetch'} refreshData={() => {}} />
        ) : reportConfigs.configs && colDefs.length > 0 ? (
          <PivotTable />
        ) : null}
      </Modal.Body>
    </Modal>
  );
};

export default ReportDialog;

const ToolbarWrapper = styled.div`
  display: flex;
  align-items: center;

  .btn-toolbar-item {
    background: #ededed;
    color: #000000;
    border: 1px solid #e1e1e1;
    margin: 0px 5px 0px;
  }

  .export-dropdown-container {
    margin: 0 5px;

    .dropdown-toggle {
      background: #ededed;
      color: #000000;
      border: 1px solid #e1e1e1;
      box-shadow: none;

      &:hover,
      &:focus,
      &:active,
      &.show {
        background: #e0e0e0 !important;
        color: #000000 !important;
        border: 1px solid #e1e1e1 !important;
        box-shadow: none !important;
      }

      &::after {
        margin-left: 0.5em;
        vertical-align: middle;
      }
    }
  }

  .dropdown-menu {
    min-width: 8rem;
    padding: 0.3rem 0;
    border-radius: 4px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  }

  .dropdown-item {
    padding: 0.25rem 1rem;
    font-size: 0.9rem;
    color: ${({ theme }: any) => theme.dropdown.color};
    background-color: ${({ theme }: any) => theme.dropdown.background_color};

    &:hover {
      color: ${({ theme }: any) => theme.dropdown.background_color};
      background-color: ${({ theme }: any) => theme.dropdown.color};
    }
  }
`;
